import { useEffect, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
  ArrowRight,
  BarChart3,
  Clock3,
  FileBox,
  FolderClock,
  Gauge,
  Leaf,
  Menu,
  PackageOpen,
  Percent,
  Plus,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  WalletCards,
  X,
  Zap,
} from 'lucide-react';
import AuthPanel from './components/AuthPanel';
import Brand from './components/Brand';
import Catalog from './components/Catalog';
import CostProfiles from './components/CostProfiles';
import Insights from './components/Insights';
import Materials from './components/Materials';
import PricingCalculator from './components/PricingCalculator';
import SavedEstimates from './components/SavedEstimates';
import UploadStarter from './components/UploadStarter';
import { auth, firebaseIsConfigured } from './lib/firebase';
import { APP_VERSION } from './generated/buildInfo';

const navigation = [
  { id: 'overview', label: 'Overview', icon: Gauge, available: true },
  { id: 'calculator', label: 'Calculator', icon: WalletCards, available: true, badge: 'Live' },
  { id: 'estimates', label: 'Estimates', icon: FolderClock, available: true },
  { id: 'catalog', label: 'Catalog', icon: PackageOpen, available: true },
  { id: 'cost-profiles', label: 'Cost profiles', icon: Settings2, available: true },
  { id: 'materials', label: 'Materials', icon: Leaf, available: true },
  { id: 'insights', label: 'Insights', icon: BarChart3, available: true },
];

function routeFromHash() {
  const hash = window.location.hash.replace('#', '');
  const [view, itemId] = hash.split('/');
  if (view === 'cost-profiles') return { view: 'cost-profiles', estimateId: '' };
  if (view === 'estimates') return { view: 'estimates', estimateId: '' };
  if (view === 'catalog') return { view: 'catalog', estimateId: '' };
  if (view === 'materials') return { view: 'materials', estimateId: '' };
  if (view === 'insights') return { view: 'insights', estimateId: '' };
  if (view === 'calculator') return { view: 'calculator', estimateId: itemId ? decodeURIComponent(itemId) : '' };
  return { view: 'overview', estimateId: '' };
}

function Overview({ onNavigate, onUpload }) {
  return (
    <>
      <section className="hero-grid">
        <div className="hero-copy">
          <p className="eyebrow">3D print pricing, rebuilt</p>
          <h1>Know the cost.<br /><span>Protect the margin.</span></h1>
          <p>Turn real print data into a price you can confidently quote, sell, and revisit later.</p>
          <div className="hero-copy__actions">
            <button className="button button--primary button--large" type="button" onClick={() => onNavigate('calculator')}><Plus size={19} /> New estimate</button>
            <button className="button button--ghost button--large" type="button" onClick={() => onNavigate('estimates')}>Open saved estimates <ArrowRight size={18} /></button>
          </div>
          <div className="trust-row">
            <span><ShieldCheck size={17} /> Private by default</span>
            <span><Zap size={17} /> Live recalculation</span>
            <span><Percent size={17} /> Margin-first pricing</span>
          </div>
        </div>

        <section className="upload-card brushed-panel">
          <div className="section-heading">
            <div><p className="eyebrow">Start a calculation</p><h2>Upload your print</h2></div>
            <div className="step-badge">Step 1 of 4</div>
          </div>
          <UploadStarter onFile={onUpload} onManualEntry={() => onNavigate('calculator')} />
        </section>
      </section>

      <section className="metric-grid" aria-label="Pricing workflow">
        <article className="metric-card"><span className="metric-card__icon metric-card__icon--green"><FileBox size={20} /></span><div><p>Material cost</p><strong>Your filament</strong><small>Calculated from the weight you import or enter</small></div></article>
        <article className="metric-card"><span className="metric-card__icon metric-card__icon--purple"><Zap size={20} /></span><div><p>Electricity</p><strong>Your local rate</strong><small>Calculated from print time and power draw</small></div></article>
        <article className="metric-card"><span className="metric-card__icon"><Clock3 size={20} /></span><div><p>Machine time</p><strong>Your printer</strong><small>Includes the wear reserve in your profile</small></div></article>
        <article className="metric-card metric-card--featured"><span className="metric-card__icon metric-card__icon--green"><Percent size={20} /></span><div><p>Target margin</p><strong>Your goal</strong><small>Applied only after you choose the percentage</small></div></article>
      </section>

      <section className="content-grid">
        <article className="pricing-preview brushed-panel">
          <div className="section-heading">
            <div><p className="eyebrow">Pricing workspace</p><h2>Built around your real costs</h2></div>
            <button className="button button--ghost button--small" type="button" onClick={() => onNavigate('cost-profiles')}>Edit defaults</button>
          </div>
          <div className="input-preview-grid">
            <div className="input-preview"><span>Filament cost</span><strong>From your profile</strong><small>Enter the price and weight of your spool</small></div>
            <div className="input-preview"><span>Electricity rate</span><strong>From your bill</strong><small>Use your actual cost per kWh</small></div>
            <div className="input-preview"><span>Machine wear</span><strong>Set by you</strong><small>Choose an hourly reserve for the printer</small></div>
            <div className="input-preview input-preview--purple"><span>Desired margin</span><strong>Set by you</strong><small>Choose the portion of the selling price to keep</small></div>
          </div>
          <div className="price-output">
            <div><span>Break-even</span><strong>Calculated</strong></div>
            <div className="price-output__recommended"><span>Recommended price</span><strong>Ready when you are</strong><small>Uses only the values you provide</small></div>
            <div><span>Estimated profit</span><strong>Calculated</strong></div>
          </div>
        </article>

        <article className="recent-card metal-panel">
          <div className="section-heading"><div><p className="eyebrow">Saved pricing</p><h2>Your estimates</h2></div><button type="button" onClick={() => onNavigate('estimates')}>Open</button></div>
          <div className="recent-list">
            <div className="recent-item recent-item--empty">
              <span className="recent-item__thumb"><FolderClock size={22} /></span>
              <span className="recent-item__copy"><strong>No saved estimates yet</strong><small>Your own estimates will appear here after you save them.</small></span>
            </div>
          </div>
          <button className="button button--purple button--full" type="button" onClick={() => onNavigate('estimates')}><FolderClock size={18} /> Browse saved estimates</button>
        </article>
      </section>
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState(null);
  const [mobileNav, setMobileNav] = useState(false);
  const [route, setRoute] = useState(routeFromHash);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [pendingProduct, setPendingProduct] = useState(null);
  const activeView = route.view;

  useEffect(() => {
    if (!auth) return undefined;
    return onAuthStateChanged(auth, setUser);
  }, []);

  useEffect(() => {
    const onHashChange = () => setRoute(routeFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const navigateTo = (view, options = {}) => {
    const estimateId = view === 'calculator' ? String(options.estimateId || '') : '';
    setRoute({ view, estimateId });
    setMobileNav(false);
    const nextHash = view === 'overview'
      ? ''
      : view === 'calculator' && estimateId
        ? `#calculator/${encodeURIComponent(estimateId)}`
        : `#${view}`;
    window.history.pushState(null, '', `${window.location.pathname}${window.location.search}${nextHash}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNav = (item) => {
    if (!item.available) return;
    navigateTo(item.id);
  };

  const startEstimateFromFile = (file) => {
    if (!file) return;
    setPendingProduct(null);
    setPendingUpload({
      file,
      token: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    });
    navigateTo('calculator');
  };

  const startEstimateFromProduct = (product) => {
    if (!product) return;
    setPendingUpload(null);
    setPendingProduct({ product, token: `${Date.now()}-${Math.random().toString(36).slice(2)}` });
    navigateTo('calculator');
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="icon-button topbar__menu" type="button" onClick={() => setMobileNav(true)} aria-label="Open navigation">
          <Menu size={22} />
        </button>
        <button className="brand-button" type="button" onClick={() => navigateTo('overview')} aria-label="MakerMargin overview"><Brand /></button>
        <div className="topbar__actions">
          <button className="search-pill" type="button"><Search size={17} /><span>{activeView === 'cost-profiles' ? 'Search cost profiles' : activeView === 'calculator' ? 'Search estimate' : activeView === 'estimates' ? 'Search saved estimates' : activeView === 'catalog' ? 'Search product catalog' : activeView === 'materials' ? 'Search material inventory' : activeView === 'insights' ? 'Explore business insights' : 'Search workspace'}</span><kbd>⌘K</kbd></button>
          {user ? (
            <div className="account-chip">
              <div className="account-chip__avatar">{(user.displayName || user.email || 'M').slice(0, 1).toUpperCase()}</div>
              <div><strong>{user.displayName || 'Maker'}</strong><span>{user.email}</span></div>
              <button type="button" onClick={() => signOut(auth)}>Sign out</button>
            </div>
          ) : (
            <>
              <button className="button button--ghost topbar__signin" type="button" onClick={() => setAuthMode('signin')}>Sign in</button>
              <button className="button button--primary" type="button" onClick={() => setAuthMode('signup')}>Create account</button>
            </>
          )}
        </div>
      </header>

      <aside className={`sidebar ${mobileNav ? 'sidebar--open' : ''}`}>
        <div className="sidebar__mobile-head">
          <Brand />
          <button className="icon-button" onClick={() => setMobileNav(false)} aria-label="Close navigation"><X size={20} /></button>
        </div>
        <nav aria-label="Primary navigation">
          {navigation.map(({ id, label, icon: Icon, available, badge }) => (
            <button className={`nav-item ${activeView === id ? 'nav-item--active' : ''} ${!available ? 'nav-item--disabled' : ''}`} type="button" key={id} onClick={() => handleNav({ id, available })} aria-current={activeView === id ? 'page' : undefined} aria-disabled={!available}>
              <Icon size={19} /><span>{label}</span>{badge && <small className={badge === 'New' ? 'nav-item__new' : ''}>{badge}</small>}
            </button>
          ))}
        </nav>
        <div className="sidebar__card metal-panel">
          <Sparkles size={21} />
          <strong>V1 Workspace Live</strong>
          <p>Price prints, reuse products, track materials, and protect margin from one private workspace.</p>
        </div>
        <p className="sidebar__version">MakerMargin Live · v{APP_VERSION}</p>
      </aside>
      {mobileNav && <button className="sidebar-scrim" aria-label="Close navigation" onClick={() => setMobileNav(false)} />}

      <main className={`workspace ${activeView === 'cost-profiles' ? 'workspace--profiles' : activeView === 'calculator' ? 'workspace--calculator' : activeView === 'estimates' ? 'workspace--estimates' : activeView === 'catalog' ? 'workspace--catalog' : activeView === 'materials' ? 'workspace--materials' : activeView === 'insights' ? 'workspace--insights' : ''}`}>
        {!firebaseIsConfigured && (
          <div className="config-banner"><ShieldCheck size={18} /><span>Interface preview is live. Add the Firebase environment variables in Cloudflare Pages to enable accounts.</span></div>
        )}

        {activeView === 'cost-profiles' ? (
          <CostProfiles user={user} onRequestAuth={setAuthMode} />
        ) : activeView === 'calculator' ? (
          <PricingCalculator
            user={user}
            onRequestAuth={setAuthMode}
            onNavigate={navigateTo}
            loadEstimateId={route.estimateId}
            onNewEstimate={() => { setPendingUpload(null); setPendingProduct(null); navigateTo('calculator'); }}
            initialUpload={pendingUpload}
            onUploadConsumed={() => setPendingUpload(null)}
            initialProduct={pendingProduct}
            onProductConsumed={() => setPendingProduct(null)}
          />
        ) : activeView === 'estimates' ? (
          <SavedEstimates
            user={user}
            onRequestAuth={setAuthMode}
            onOpen={(estimateId) => navigateTo('calculator', { estimateId })}
            onNew={() => navigateTo('calculator')}
          />
        ) : activeView === 'catalog' ? (
          <Catalog user={user} onRequestAuth={setAuthMode} onNavigate={navigateTo} onPriceProduct={startEstimateFromProduct} />
        ) : activeView === 'materials' ? (
          <Materials user={user} onRequestAuth={setAuthMode} />
        ) : activeView === 'insights' ? (
          <Insights user={user} onRequestAuth={setAuthMode} onNavigate={navigateTo} />
        ) : (
          <Overview onNavigate={navigateTo} onUpload={startEstimateFromFile} />
        )}
      </main>

      {authMode && <AuthPanel mode={authMode} onClose={() => setAuthMode(null)} onModeChange={setAuthMode} />}
    </div>
  );
}

export default App;
